import React from 'react';
import { Box, IconButton, Inline, Link, Stack, StatusMessage } from '@99designs/emu';
import { IconSent1 } from '@99designs/icons';
import {
    CircumventionWarning,
    CreateMessageFn,
    useMessageInputWithCircumventionWarning,
} from '@99designs/messaging';
import { AutosizeTextArea } from '@99designs/spa-common';
import { TrackedLink, trackEvent } from '@99designs/tracking';
import { ViewerContext_UserFragment } from '@99designs/viewer';
import { useFileOverlayTracking } from '../../../FileOverlayTrackingContext';

export interface NewAnnotationFormProps {
    viewer: ViewerContext_UserFragment;
    createAnnotation: CreateMessageFn;
    onCancel: () => void;
    initialMessageValue?: string;
    error: boolean;
}

function NewAnnotationForm({
    viewer,
    createAnnotation,
    onCancel,
    error,
    initialMessageValue = '',
}: NewAnnotationFormProps) {
    const { annotationSentTracking } = useFileOverlayTracking();
    const {
        message,
        setMessage,
        showCircumventionWarning,
        inputOnChange,
        inputOnKeyDown,
        inputOnSubmit,
        matches,
    } = useMessageInputWithCircumventionWarning({
        initialMessageValue,
        createMessage: createAnnotation,
        viewer,
        tracking: annotationSentTracking,
        trackEvent,
    });

    return (
        <Box paddingY="0.5bu">
            <Stack space="1bu">
                {showCircumventionWarning && (
                    <CircumventionWarning
                        user={viewer}
                        message={message}
                        triggers={matches}
                        location={
                            annotationSentTracking
                                ? annotationSentTracking.properties.location
                                : 'Annotation canvas form'
                        }
                        trackEvent={trackEvent}
                        TrackedLink={TrackedLink}
                    />
                )}
                <AutosizeTextArea
                    error={error}
                    placeholder={__('Leave a comment')}
                    onChange={inputOnChange}
                    onKeyDown={inputOnKeyDown}
                    value={message}
                    autoComplete="off"
                    maxLength={100000}
                    autoFocus
                />
                {error && (
                    <StatusMessage type="caution">
                        {__('Sorry, something went wrong.')}
                    </StatusMessage>
                )}

                <Inline space="1bu">
                    <IconButton
                        icon={IconSent1}
                        aria-label={__('Send')}
                        onClick={inputOnSubmit}
                        appearance="primary"
                        type="submit"
                    />
                    <Link
                        onClick={() => {
                            setMessage('');
                            onCancel();
                        }}
                    >
                        {__('Cancel')}
                    </Link>
                </Inline>
            </Stack>
        </Box>
    );
}

export default NewAnnotationForm;
